import type { ComponentType, JSX } from 'react';

import { cn } from '@/lib/utils';

/**
 * The one toggle primitive of the room bars (the unified control bar): pressed reads visibly
 * lit (solid bg-primary, or the variant's solid color when the state has its own semantics -
 * muted red, live-share emerald, raised-hand amber), released reads dimmed. Every toggle in
 * both the meeting pill and the webinar bar renders through this component, so the lit/dim
 * states cannot drift per button; tooltips are plain title attributes (i18n upstream).
 */
export type ToggleVariant = 'neutral' | 'danger' | 'accent' | 'amber';

export interface ToggleButtonProps {
  readonly testid: string;
  /** Accessible name; stays free of the hotkey suffix so labels stay stable. */
  readonly label: string;
  /** Tooltip text; usually the label plus the hotkey hint. */
  readonly tooltip?: string | undefined;
  readonly icon: ComponentType<{ className?: string }>;
  /** The toggled-on state (pressed), even when the toggle means "off" for media. */
  readonly pressed: boolean;
  readonly onClick?: (() => void) | undefined;
  readonly disabled?: boolean | undefined;
  /** Color of the lit state; the media-off toggles read red, an active share emerald. */
  readonly variant?: ToggleVariant | undefined;
  /** Count bubble on the top-right corner (unread chat, participants, lobby). */
  readonly badge?: string | undefined;
  /** Mobile sizing: 36px instead of 40px. */
  readonly compact?: boolean | undefined;
  /**
   * Render override for wrapper components (dropdown triggers): receives the styled button
   * and must render it. When absent the button renders with its title tooltip.
   */
  readonly children?: ((button: JSX.Element) => JSX.Element) | undefined;
}

const LIT_CLASSES: Readonly<Record<ToggleVariant, string>> = {
  neutral: 'bg-primary text-primary-foreground hover:bg-primary/90',
  danger: 'bg-red-500 text-white hover:bg-red-600',
  accent: 'bg-emerald-500 text-zinc-950 hover:bg-emerald-400',
  amber: 'bg-amber-400 text-zinc-950 hover:bg-amber-300',
};

export function ToggleButton({
  testid,
  label,
  tooltip,
  icon: Icon,
  pressed,
  onClick,
  disabled = false,
  variant = 'neutral',
  badge,
  compact = false,
  children,
}: ToggleButtonProps): JSX.Element {
  const button = (
    <button
      type="button"
      data-testid={testid}
      aria-label={label}
      aria-pressed={pressed}
      aria-disabled={disabled || undefined}
      title={compact ? undefined : (tooltip ?? label)}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative flex items-center justify-center rounded-xl transition-all',
        compact ? 'h-9 w-9 max-[359px]:w-8' : 'h-10 w-10',
        pressed ? LIT_CLASSES[variant] : 'text-zinc-300 hover:bg-white/10 hover:text-white',
        disabled && 'cursor-not-allowed text-zinc-600 hover:bg-transparent hover:text-zinc-600',
      )}
    >
      <Icon className="size-[18px]" />
      {badge !== undefined ? (
        <span
          data-testid={`${testid}-badge`}
          className="absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-[9.5px] font-bold text-zinc-950"
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
  return children ? (
    children(button)
  ) : (
    // A plain <div> wrapper keeps the button's own title tooltip when no render override is given.
    <div>{button}</div>
  );
}
